import React from "react";

import { LocalDateTime } from "@js-joda/core";
import { MdmDeviceDto, useGetDeviceStatusRecordsQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import Plot from "react-plotly.js";
import { useResizeDetector } from "react-resize-detector";
import { T } from "services/lang/LanguageService";

type StatusHistoryProps = {
  device: Partial<MdmDeviceDto>
}

const StatusHistory: React.FC<StatusHistoryProps> = ({ device }) => {
  const { width, ref } = useResizeDetector();
  const { data, loading } = useGetDeviceStatusRecordsQuery({
    variables: { id: device.id! },
    skip: !device.id
  })

  if (loading) return <Button ref={ref} loading={loading} className="p-button-text text-gray-800" />
  if (!data || data?.mdmDeviceStatusRecords.length === 0) return <span>{T("word.no-records")}</span>

  const sortData = [...data.mdmDeviceStatusRecords].sort((a, b) => a.timestamp > b.timestamp ? 1 : -1)
  const dates = sortData.map(d => new Date(d.timestamp));
  const statuses = sortData.map(d => d.status === "ONLINE" ? 1 : 0);
  const lastStatus = statuses.at(-1)
  const xValues = [...dates, new Date()];
  const yValues = [...statuses, lastStatus!];
 
  return (
    <div ref={ref}>
      <Plot
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ["lasso2d", "select2d"],
        }}
        data={
          [
            {
              type: "scatter",
              name: "Status",
              mode: "lines",
              showlegend: false,
              line: {
                shape: "hv",
                color: "teal",
              },
              x: xValues,
              y: yValues,
              opacity: 0.7,
            },
          ]
        }
        layout={{
          margin: { t: 30, b: 80, l: 60, r: 0, pad: 10 },
          yaxis: {
            tickvals: [0, 1],
            ticktext: ["Offline", "Online"],
            fixedrange: true,
          },
          xaxis: {
            type: "date",
            rangeselector: {
              buttons: [
                {
                  step: "hour",
                  count: 24,
                },
                {
                  step: "day",
                  count: 7,
                },
                {
                  step: "month",
                  label: "1mo",
                },
                {
                  step: "year",
                },
                {
                  step: "all",
                },
              ],
            },
            rangeslider: {
              visible: true,
            },
            range: [LocalDateTime.now().minusDays(7).toString(), LocalDateTime.now().toString()],
          },
          width: width!,
          height: 400,
          hovermode: "closest",
        }}
      />
    </div>
  );
}

export default StatusHistory;